import { useState } from "react";
import { DynamicGrid } from "../../dynamicGrid";
import { useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  assessmentFormStandardAnswersQueryOptions,
  assessmentsFormQueryOptions,
  updateStandardAnswersMutationOptions,
  updateAssessmentAnswersMutationOptions,
  type AwaitingResponseAssessment,
} from "../trustAPI";
import { DynamicForm, type Answer, type AnswerRequest } from "../../dynamicForm";
import { Drawer } from "../../shared/Drawer";
import { Box, Button, Stack, Typography, type SxProps, lighten } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { TrustSignDialog } from "../TrustSignDialog";
import * as FormDialog from "../../shared/FormDialog";
import { AssessmentDrawerHeader } from "./AssessmentDrawerHeader";

type AwaitingResponseDrawerContentProps = {
  assessment: AwaitingResponseAssessment;
  onSignSuccess: ({
    standardAnswers,
    customAnswerRequests,
  }: {
    standardAnswers: Answer[];
    customAnswerRequests: AnswerRequest[];
  }) => void;
};

export const AwaitingResponseDrawerContent = ({ assessment, onSignSuccess }: AwaitingResponseDrawerContentProps) => {
  const selectedRowId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const assessmentFormQuery = useQuery(assessmentsFormQueryOptions());
  const standardAnswersQuery = useQuery(assessmentFormStandardAnswersQueryOptions());

  const updateAnswersMutation = useMutation(updateAssessmentAnswersMutationOptions(selectedRowId));
  const updateStandardAnswersMutation = useMutation(updateStandardAnswersMutationOptions());

  const [isSignDialogOpen, setIsSignDialogOpen] = useState(false);

  if (!assessmentFormQuery.data || !standardAnswersQuery.data) return <Drawer.Loading />;

  const standardAnswers = standardAnswersQuery.data.answers;
  const customAnswerByQuestionId = new Map(assessment.customAnswers.map(answer => [answer.questionId, answer]));

  return (
    <>
      <DynamicForm.Provider
        key={assessment.id}
        answers={mergeStandardAnswersWithCustom({
          standard: standardAnswers,
          custom: assessment.customAnswers,
        })}
        isSaving={updateAnswersMutation.isPending || updateStandardAnswersMutation.isPending}
        isSaveError={updateAnswersMutation.isError || updateStandardAnswersMutation.isError}
        onSave={answerRequest => updateAnswersMutation.mutateAsync([answerRequest])}
      >
        {({ formState }) => (
          <>
            <AssessmentDrawerHeader
              title={assessment.requestedBy.organisation.name}
              status={DynamicForm.formStateToDrawerHeaderStatus(formState)}
            />
            <Drawer.Content sx={{ pb: 0 }}>
              <DynamicForm.InlineForm
                variant="secondary"
                id={selectedRowId}
                form={assessmentFormQuery.data}
                renderSlots={{
                  afterInput: questionCard => {
                    const customAnswer = customAnswerByQuestionId.get(questionCard.id);

                    if (standardAnswers.length === 0) return null;

                    if (!customAnswer) return null;

                    return (
                      <Stack sx={{ gap: 1 }}>
                        <Button
                          sx={{
                            "fontSize": theme => theme.typography.pxToRem(12),
                            "backgroundColor": theme => lighten(theme.palette.primary["50"], 0.3),
                            "&:hover": { backgroundColor: theme => lighten(theme.palette.primary["50"], 0.1) },
                          }}
                          size="small"
                          variant="text"
                          onClick={() => {
                            updateStandardAnswersMutation.mutate([DynamicForm.answerToAnswerRequest(customAnswer)]);
                          }}
                        >
                          {t("Trust.Drawer.SaveAsStandardAnswer")}
                        </Button>
                        {updateStandardAnswersMutation.isError &&
                          updateStandardAnswersMutation.variables.some(
                            ({ questionId }) => questionId === questionCard.id
                          ) && <DynamicForm.ErrorMessage />}
                      </Stack>
                    );
                  },
                }}
              />

              {(standardAnswers.length > 0 || assessment.customAnswers.length > 0) && (
                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    width: "100%",
                    backgroundColor: theme => theme.palette.background.paper,
                  }}
                >
                  <Drawer.Divider />
                  <Stack direction="row" sx={{ justifyContent: "flex-end", p: 2 }}>
                    <Button
                      sx={{ minWidth: "150px" }}
                      variant="contained"
                      color="primary"
                      onClick={() => setIsSignDialogOpen(true)}
                    >
                      {t("Trust.Card.SignAndSend")}
                    </Button>
                  </Stack>
                </Box>
              )}
            </Drawer.Content>
          </>
        )}
      </DynamicForm.Provider>
      {isSignDialogOpen && (
        <TrustSignDialog
          assessmentId={assessment.id}
          onClose={() => setIsSignDialogOpen(false)}
          onSigned={() =>
            onSignSuccess({
              standardAnswers: standardAnswers,
              customAnswerRequests: assessment.customAnswers.map(DynamicForm.answerToAnswerRequest),
            })
          }
        />
      )}
    </>
  );
};

const mergeStandardAnswersWithCustom = (answers: { standard: Answer[]; custom: Answer[] }): Answer[] => {
  const customAnswersByQuestionId = new Map(answers.custom.map(c => [c.questionId, c]));
  const standardWithoutCustom = answers.standard.filter(
    standardAnswer => !customAnswersByQuestionId.has(standardAnswer.questionId)
  );

  return [...standardWithoutCustom, ...answers.custom];
};

type MakeAllCustomAnswersStandardDialogProps = {
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

const standardAnswerDialogTextSx: SxProps<Theme> = {
  textWrap: "pretty",
  color: theme => theme.palette.text.secondary,
  fontSize: theme => theme.typography.pxToRem(14),
};

export const MakeAllCustomAnswersStandardDialog = ({
  onCancel,
  onConfirm,
}: MakeAllCustomAnswersStandardDialogProps) => {
  const { t } = useTranslation();
  const confirmMutation = useMutation({ mutationFn: onConfirm });

  return (
    <FormDialog.Dialog isOpen maxWidth="520px">
      <FormDialog.Title>{t("Trust.StandardAnswerDialog.Title")}</FormDialog.Title>
      <FormDialog.Content>
        <FormDialog.FormError isError={confirmMutation.isError} />
        <Typography sx={standardAnswerDialogTextSx}>{t("Trust.StandardAnswerDialog.Description")}</Typography>
      </FormDialog.Content>
      <FormDialog.ActionButtons sx={{ marginTop: 3 }}>
        <Button variant="outlined" onClick={onCancel}>
          {t("Trust.Drawer.Cancel")}
        </Button>
        <Button variant="contained" loading={confirmMutation.isPending} onClick={() => confirmMutation.mutate()}>
          {t("Trust.StandardAnswerDialog.SaveAsStandardAnswers")}
        </Button>
      </FormDialog.ActionButtons>
    </FormDialog.Dialog>
  );
};
